import { useEffect, useMemo, useRef, useState } from "react";
import type { ActorGrafoSiger, GrafoSiger as DatosGrafo, LazoSiger, OfacDeActor, RelacionOfacSiger, VinculoSiger } from "../api.ts";
import { C, NODO, fecha, st } from "../theme.ts";
import { rutaAHash } from "../rutas.ts";
import { anchoChip } from "./chips.ts";
import { CONTRA_ES, NIVEL_ES, NOTA_NO_ANCLA, baseOfac, esAncla, etiquetaSenal, gruposAnclas, rojoDeIdentidad, textoRevision } from "./senales-siger.ts";
import {
  CAMPO_ES, GEOMETRIA_RED, NODO_RED, OCULTOS_DE_ENTRADA, ORIGEN_ES, acomodarRed, chipsActor, chipsColocados, corto, curva,
  etiquetaLazo as etiquetaLazoRed, etiquetaNodo as etiquetaNodoRed, gruposRed, nodoEnLista, satChip, satCoincidencia, satOrden, satTexto,
  terminado, textoTipo, tipoDe, tonoDe, tonoDeTipo, tonoSat, type Chip, type TonoRed,
} from "../../../shared/piezas/red.ts";
import { DE_NEXO, LEYENDA_RED as L } from "../../../shared/rotulos/red.ts";
import { MARCA_POSIBLE, MARCA_POSIBLE_AYUDA } from "../../../shared/rotulos/buscador.ts";
import { texto } from "../../../shared/rotulos/ficha.ts";

/**
 * GrafoSiger — estructura documentada en el SIGER alrededor de una entidad OFAC, extraída por Claude y
 * consolidada por reglas (corrida vigente de nexo.siger_*):
 *   sancionado → lazo directo (empresas, cargos, relaciones personales) → quienes se ligan a través de cada uno.
 * Muestra por separado la IDENTIDAD (¿el nodo es una entidad OFAC y cómo lo sabemos?) y el VÍNCULO
 * (tipo de relación, vigencia, evidencia del acta). Sin revisión humana.
 *
 * 2026-10-02 (Bernardo: «la letra no comunica realmente qué significa»): sin categorías A–D. Las ligas se agrupan por tipo de
 * relación, los mismos grupos y conteos que la lista «Registro de comercio», y el color dice su peso (nexo.tipo_liga_orden):
 * rojo capital, cargo, poder o familia; ámbar otras relaciones directas o a través de alguien; gris contexto.
 *
 * Etapa 4, ajustes 4d (2026-10-05): la parte pura (forma, rótulos, señales de los nodos, listas del SAT y acomodo) vive en
 * app/shared/piezas/red.ts, la misma que pinta Argus Team; aquí sólo se pinta con el tema de Nexo. La base manda `corroborada` en el
 * grafo (20261005120000); rojoDeIdentidad se queda como respaldo para una base anterior. Una relación por un ancla sin corroborar lleva
 * la marca «posible» y la identidad del ancla sin corroborar ya no va en rojo.
 */

const COLOR: Record<TonoRed, string> = { rojo: C.red, ambar: C.amber, gris: C.muted };
const colorDe = (v: VinculoSiger): string => COLOR[tonoDe(v)];
const etiquetaLazo = (l: LazoSiger): string => etiquetaLazoRed(l, fecha);
const etiquetaNodo = (l: LazoSiger, nodo: string, A: Record<string, ActorGrafoSiger>): string => etiquetaNodoRed(l, nodo, A, fecha);
const { xRaiz: X_RAIZ, wRaiz: W_RAIZ, xN1: X_N1, wN1: W_N1, xN2: X_N2, wN2: W_N2, ancho: ANCHO, fila: FILA, alto: ALTO } = GEOMETRIA_RED;

type Sel = { tipo: "actor"; id: string; v: VinculoSiger | null } | { tipo: "raiz" } | null;

export function GrafoSiger({ datos, entidadId, raizNombre, actorInicial, incrustado = false }: {
  datos: DatosGrafo; entidadId: string; raizNombre: string; actorInicial?: string | undefined;
  /** Dentro de la sección «Registro de comercio» (sin tarjeta propia ni título). */
  incrustado?: boolean;
}) {
  // Llegada desde el buscador: la persona o empresa ya seleccionada, su tipo de relación visible y el grafo a la vista.
  const vInicial = actorInicial ? datos.vinculos.find((v) => v.actor_id === actorInicial) ?? null : null;
  // Los grupos: los de la base (mismos conteos que la lista); si no vienen, los que traen las ligas dibujadas.
  const grupos = useMemo(() => gruposRed(datos), [datos]);
  const [ocultos, setOcultos] = useState<Set<string>>(() => new Set([...OCULTOS_DE_ENTRADA].filter((t) => !vInicial || tipoDe(vInicial) !== t)));
  const [soloVigentes, setSoloVigentes] = useState(false);
  const [sel, setSel] = useState<Sel>(actorInicial && datos.actores[actorInicial] ? { tipo: "actor", id: actorInicial, v: vInicial } : null);
  const caja = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (actorInicial) caja.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [actorInicial]);
  const A = datos.actores;

  const { filas, sinActa, lejanos, alto, yRaiz } = useMemo(() => acomodarRed(datos, ocultos, soloVigentes), [datos, ocultos, soloVigentes]);

  const anclas = datos.anclas.filter((x) => esAncla(x.nivel));
  const mejor = anclas[0];
  // 4d: la identidad del ancla va en rojo sólo corroborada (antes, siempre).
  const colorMejor = mejor && rojoDeIdentidad(mejor) ? C.red : C.amber;
  const expAnclas = new Set(anclas.flatMap((x) => A[x.actor_id]?.expedientes ?? []));
  const totalLigas = grupos.reduce((n, g) => n + g.ligas, 0);
  const declaradas = new Set((datos.relaciones_ofac ?? []).map((r) => r.actor_id).filter(Boolean));

  return (
    <div ref={caja} style={incrustado ? { marginTop: 14 } : { ...st.tarjeta, marginTop: 12 }}>
      <p style={incrustado ? { fontSize: 14, fontWeight: 700, margin: "0 0 6px", color: C.ink } : st.eyebrow}>
        {incrustado ? "El dibujo de la red" : "Registro Público de Comercio"} · {totalLigas === 1 ? "1 relación" : `${String(totalLigas)} relaciones`}
        {(datos.relaciones_ofac ?? []).length > 0 ? ` · ${String((datos.relaciones_ofac ?? []).length)} relaciones que declara OFAC` : ""}
      </p>
      <div style={{ fontSize: 12.5, color: C.ink, margin: "0 0 10px", lineHeight: 1.6 }}>
        <b>{DE_NEXO.quienEs}</b>{" "}
        {mejor ? (
          <>
            {A[mejor.actor_id]?.nombre} — identidad <b style={{ color: colorMejor }}>{NIVEL_ES[mejor.nivel] ?? mejor.nivel}</b> por{" "}
            <b>{baseOfac(mejor.senales, mejor.revision).larga}</b>; aparece en los documentos de {String(expAnclas.size)} empresas
            {anclas.length > 1 ? ` (${String(anclas.length)} personas encontradas que el sistema no pudo unir sin un identificador común)` : ""}.{" "}
            {(() => {
              const fs = [...new Map([...(datos.sat_entidad ?? []), ...anclas.flatMap((x) => A[x.actor_id]?.sat ?? [])].map((f) => [`${f.lista}/${f.categoria}`, f])).values()]
                .sort((x, y) => satOrden(x) - satOrden(y));
              return fs.length > 0 ? (
                <span>En el SAT: <b style={{ color: COLOR[tonoSat(fs[0]!)] }}>{fs.map((f) => satTexto(f)).join("; ")}</b>. </span>
              ) : null;
            })()}
            <a style={{ color: C.tiel, cursor: "pointer" }} onClick={() => setSel({ tipo: "raiz" })}>ver detalle</a>
          </>
        ) : (
          <span style={{ color: C.muted }}>sin identidad confirmada o alta en el Registro Público de Comercio.</span>
        )}
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", fontSize: 12, color: C.muted, marginBottom: 8 }}>
        {grupos.map((g) => (
          <label key={g.tipo} style={{ display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={!ocultos.has(g.tipo)} onChange={(ev) => {
              const n = new Set(ocultos);
              if (ev.target.checked) n.delete(g.tipo); else n.add(g.tipo);
              setOcultos(n);
            }} />
            <span style={{ width: 10, height: 3, background: COLOR[tonoDeTipo(g.tipo)], display: "inline-block" }} />
            {g.texto} ({String(g.ligas)})
          </label>
        ))}
        <label style={{ display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
          <input type="checkbox" checked={soloVigentes} onChange={(ev) => setSoloVigentes(ev.target.checked)} />
          {DE_NEXO.soloVigentes}
        </label>
      </div>
      <details style={{ margin: "0 0 8px" }}>
        <summary style={{ fontSize: 12, color: C.tiel, cursor: "pointer" }}>{DE_NEXO.comoLeer}</summary>
        <Leyenda />
      </details>
      <div style={{ overflow: "auto", maxHeight: 680, border: `1px solid ${C.line}`, borderRadius: 10, background: "#fff" }}>
        <svg width={ANCHO} height={alto} role="img" aria-label="Estructura en el Registro Público de Comercio alrededor de la entidad sancionada" style={{ display: "block" }}>
          {filas.map((f) => {
            const ym = f.y0 + ((f.n - 1) * FILA) / 2 + FILA / 2;
            const l0 = f.v.camino[0];
            return (
              <g key={f.v.actor_id}>
                <path d={curva(X_RAIZ + W_RAIZ, yRaiz, X_N1, ym)} fill="none" stroke={colorDe(f.v)} strokeWidth={1.8}
                  strokeDasharray={l0 && terminado(l0) ? "5 4" : undefined} opacity={0.8} />
                {f.hs.map((h, i) => {
                  const yh = f.y0 + i * FILA + FILA / 2;
                  const lh = h.camino[0];
                  return (
                    <g key={h.actor_id}>
                      <path d={curva(X_N1 + W_N1, ym, X_N2, yh)} fill="none" stroke={colorDe(h)} strokeWidth={1.3}
                        strokeDasharray={lh && terminado(lh) ? "5 4" : undefined} opacity={0.75} />
                      <Nodo a={A[h.actor_id]} v={h} etiqueta={lh ? etiquetaNodo(lh, h.actor_id, A) : ""} x={X_N2} y={yh - ALTO / 2} w={W_N2} entidad={entidadId}
                        activo={sel?.tipo === "actor" && sel.id === h.actor_id} onClick={() => setSel({ tipo: "actor", id: h.actor_id, v: h })} />
                    </g>
                  );
                })}
                <Nodo a={A[f.v.actor_id]} v={f.v} etiqueta={l0 ? etiquetaNodo(l0, f.v.actor_id, A) : ""} x={X_N1} y={ym - ALTO / 2} w={W_N1} entidad={entidadId}
                  declaradaOfac={declaradas.has(f.v.actor_id)}
                  activo={sel?.tipo === "actor" && sel.id === f.v.actor_id} onClick={() => setSel({ tipo: "actor", id: f.v.actor_id, v: f.v })} />
              </g>
            );
          })}
          {sinActa.map(({ r, y0 }) => (
            <RelacionSinActa key={r.contraparte_id} r={r} yRaiz={yRaiz} y={y0 + FILA / 2} />
          ))}
          <g transform={`translate(${String(X_RAIZ)},${String(yRaiz - 34)})`} style={{ cursor: "pointer" }} onClick={() => setSel({ tipo: "raiz" })}>
            <rect width={W_RAIZ} height={68} rx={12} fill={C.redBg} stroke={C.red} strokeWidth={2} />
            <text x={10} y={18} fontSize={10} fontWeight={700} fill={C.red}>En la lista de OFAC</text>
            <text x={10} y={34} fontSize={12} fontWeight={700} fill={C.ink}>{corto(raizNombre, 26)}</text>
            <text x={10} y={61} fontSize={9.5} fontWeight={700} fill={mejor ? colorMejor : C.red}>
              {mejor ? `Registro: ${baseOfac(mejor.senales).corta} · documentos de ${String(expAnclas.size)} empresas` : "sin ancla en el registro"}
            </text>
            {(() => {
              const ch = satChip(datos.sat_entidad);
              return ch ? (
                <g transform={`translate(10,${String(68 + 4)})`}>
                  <rect width={anchoChip(ch.t)} height={12} rx={6} fill={ch.lleno ? COLOR[ch.tono] : "#fff"} stroke={COLOR[ch.tono]} />
                  <text x={anchoChip(ch.t) / 2} y={9} fontSize={8.5} fontWeight={700} fill={ch.lleno ? "#fff" : COLOR[ch.tono]} textAnchor="middle">{ch.t}</text>
                </g>
              ) : null;
            })()}
          </g>
        </svg>
      </div>
      {lejanos.length > 0 ? (
        <p style={{ fontSize: 12, color: C.muted, margin: "6px 0 0" }}>
          {texto(DE_NEXO.lejanos, { n: lejanos.length })}
        </p>
      ) : null}
      {sel?.tipo === "actor" ? <DetalleActor id={sel.id} v={sel.v} datos={datos} /> : null}
      {sel?.tipo === "raiz" ? <DetalleAnclas datos={datos} /> : null}
      {!sel ? <p style={{ fontSize: 12, color: C.muted, margin: "8px 0 0" }}>{DE_NEXO.pulsa}</p> : null}
      {lejanos.length > 0 ? (
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12, marginTop: 8 }}>
          <tbody>
            {lejanos.slice(0, 60).map((v) => (
              <tr key={v.actor_id} style={{ borderTop: `1px solid ${C.line}`, cursor: "pointer" }} onClick={() => setSel({ tipo: "actor", id: v.actor_id, v })}>
                <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}><b style={{ color: colorDe(v) }}>{textoTipo(v)}</b></td>
                <td style={{ padding: "4px 6px", fontWeight: 600 }}>{A[v.actor_id]?.nombre}</td>
                <td style={{ padding: "4px 6px", color: C.muted }}>
                  {[...v.camino].reverse().map((l) => etiquetaLazo(l)).join(" → ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

function RelacionSinActa({ r, yRaiz, y }: { r: RelacionOfacSiger; yRaiz: number; y: number }) {
  return (
    <g>
      <path d={curva(X_RAIZ + W_RAIZ, yRaiz, X_N1, y)} fill="none" stroke={C.red} strokeWidth={1.3} strokeDasharray="3 4" opacity={0.7} />
      <a href={rutaAHash({ vista: "entidad", id: r.contraparte_id })}>
        <g transform={`translate(${String(X_N1)},${String(y - ALTO / 2)})`}>
          <title>{`${r.nombre} · relación declarada por OFAC (${r.rol}); no hay acta del Registro Público de Comercio que la documente`}</title>
          <rect width={W_N1} height={ALTO} rx={8} fill={r.en_lista === "retirada" ? "#fff" : C.redBg} stroke={r.en_lista === "retirada" ? C.muted : C.red} strokeDasharray="4 3" />
          <rect width={5} height={ALTO} rx={2} fill={r.tipo === "Person" ? NODO.Person : r.tipo === "Company" ? NODO.Company : NODO.Organization} />
          <text x={12} y={14} fontSize={11.5} fontWeight={600} fill={C.ink}>{corto(r.nombre, 46)}</text>
          <text x={12} y={26} fontSize={10} fill={C.muted}>{corto(`${r.rol} · declarada por OFAC · sin acta en el registro`, 56)}</text>
          {/* Revisión 4d (I3): quien salió de la lista dice «Estuvo…», sin rojo; en ámbar desde la escala de riesgo (es Medio). */}
          {r.en_lista === "retirada" ? (
            <g transform={`translate(12,${String(ALTO - 16)})`}>
              <rect width={anchoChip(NODO_RED.estuvo)} height={12} rx={6} fill="#fff" stroke={C.amber} />
              <text x={anchoChip(NODO_RED.estuvo) / 2} y={9} fontSize={8.5} fontWeight={700} fill={C.amber} textAnchor="middle">{NODO_RED.estuvo}</text>
            </g>
          ) : (
            <g transform={`translate(12,${String(ALTO - 16)})`}>
              <rect width={anchoChip(NODO_RED.enLista)} height={12} rx={6} fill={C.red} stroke={C.red} />
              <text x={anchoChip(NODO_RED.enLista) / 2} y={9} fontSize={8.5} fontWeight={700} fill="#fff" textAnchor="middle">{NODO_RED.enLista}</text>
            </g>
          )}
        </g>
      </a>
    </g>
  );
}

function Nodo({ a, v, etiqueta, x, y, w, entidad, activo, onClick, declaradaOfac = false }: {
  a: ActorGrafoSiger | undefined; v: VinculoSiger; etiqueta: string; x: number; y: number; w: number;
  entidad: string; activo: boolean; onClick: () => void; declaradaOfac?: boolean;
}) {
  const chips = chipsActor(a, entidad, v, declaradaOfac);
  const esOfac = nodoEnLista(a);
  const colocados = chipsColocados(chips, w);
  const resto = chips.slice(colocados.filter((c) => chips.includes(c.chip)).length);
  return (
    <g transform={`translate(${String(x)},${String(y)})`} style={{ cursor: "pointer" }} onClick={onClick}>
      <title>{`${a?.nombre ?? ""} · ${textoTipo(v)}${resto.length ? ` · ${resto.map((ch) => ch.t).join(" · ")}` : ""}${chips.some((ch) => ch.ayuda) ? ` · ${chips.filter((ch) => ch.ayuda).map((ch) => ch.ayuda).join(" · ")}` : ""}`}</title>
      <rect width={w} height={ALTO} rx={8} fill={esOfac ? C.redBg : "#fff"} stroke={activo ? C.tiel : esOfac ? C.red : C.line}
        strokeWidth={activo ? 2.5 : esOfac ? 1.6 : 1} />
      <rect width={5} height={ALTO} rx={2} fill={a?.tipo === "empresa" ? NODO.Company : NODO.Person} />
      <text x={12} y={14} fontSize={11.5} fontWeight={600} fill={C.ink}>{corto(a?.nombre, 46)}</text>
      <text x={12} y={26} fontSize={10} fill={C.muted}>{corto(etiqueta, 56)}</text>
      {colocados.map(({ chip: ch, x: x0, ancho: cw }: { chip: Chip; x: number; ancho: number }) => (
        <g key={ch.t} transform={`translate(${String(x0)},${String(ALTO - 16)})`}>
          <rect width={cw} height={12} rx={6} fill={ch.lleno ? COLOR[ch.tono] : "#fff"} stroke={COLOR[ch.tono]} />
          <text x={cw / 2} y={9} fontSize={8.5} fontWeight={700} fill={ch.lleno ? "#fff" : COLOR[ch.tono]} textAnchor="middle">{ch.t}</text>
        </g>
      ))}
    </g>
  );
}

function Leyenda() {
  const item = (color: string, texto: string, dash = false) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <svg width={22} height={8}><line x1={0} y1={4} x2={22} y2={4} stroke={color} strokeWidth={2} strokeDasharray={dash ? "4 3" : undefined} /></svg>
      {texto}
    </span>
  );
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11.5, color: C.muted, margin: "0 0 8px" }}>
      <span style={{ fontWeight: 700, color: C.ink }}>{L.relacion}</span>
      {item(C.red, L.rojo)}{item(C.amber, L.ambar)}{item(C.muted, L.gris)}
      {item(C.muted, L.terminado, true)}
      <span><b style={{ color: NODO.Person }}>▌</b> {L.persona} · <b style={{ color: NODO.Company }}>▌</b> {L.empresa}</span>
      <span>
        {L.identidad} <b style={{ color: C.red }}>{NODO_RED.enLista}</b> {L.enLista} ·{" "}
        <b style={{ color: C.amber }}>{NODO_RED.quizaEnLista}</b> {L.quizaEnLista} · <b style={{ color: C.muted }}>{NODO_RED.contradice}</b> {L.contradice} ·{" "}
        <b style={{ color: C.red }}>{NODO_RED.cincuenta}</b> {L.cincuenta} ·{" "}
        <b style={{ color: C.red }}>SAT: facturas falsas</b>/<b style={{ color: C.amber }}>SAT: no localizado…</b> {L.sat} ·{" "}
        <b style={{ color: C.amber }}>¿SAT: …?</b> {L.satPosible} ·{" "}
        <b style={{ color: C.amber }}>{NODO_RED.estuvo}</b> {L.estuvo} ·{" "}
        <b style={{ color: C.amber }}>{MARCA_POSIBLE}</b> {MARCA_POSIBLE_AYUDA} ·{" "}
        {L.punteado}
      </span>
    </div>
  );
}

function Evidencia({ l }: { l: LazoSiger }) {
  return (
    <>
      {l.evidencia.slice(0, 3).map((e, i) => (
        <div key={i} style={{ fontSize: 11.5, color: C.muted, marginLeft: 12 }}>
          {e.contraparte ? <b style={{ color: C.ink }}>{`con ${e.contraparte}${e.detalle ? ` (${corto(e.detalle, 80)})` : ""} · `}</b> : null}
          {e.archivo ?? "acta"}{e.pag != null ? ` · pág. ${String(e.pag)}` : ""}{e.fecha ? ` · ${fecha(e.fecha)}` : ""}
          {e.cita ? <span style={{ fontStyle: "italic" }}>{` · “${corto(e.cita, 180)}”`}</span> : null}
          {e.cita_verificada === false ? <b style={{ color: C.amber }}> (cita no verificada)</b> : null}
        </div>
      ))}
    </>
  );
}

function BloqueOfac({ o }: { o: OfacDeActor }) {
  const color = rojoDeIdentidad(o) ? C.red : o.nivel === "confirmada" || o.nivel === "alta" || o.nivel === "media" ? C.amber : C.muted;
  return (
    <div style={{ fontSize: 12.5, lineHeight: 1.6, color: C.ink }}>
      <b style={{ color }}>Identidad OFAC {NIVEL_ES[o.nivel] ?? o.nivel}</b> con{" "}
      <a href={rutaAHash({ vista: "entidad", id: o.entidad_id })} style={{ color, fontWeight: 700 }}>{o.nombre}</a>
      {`: ${baseOfac(o.senales, o.revision).larga}.`}
      <span style={{ color: C.muted }}>
        {` Señales: ${o.senales.map((s) => etiquetaSenal(s, o.senales)).join(", ") || "—"}.`}
        {o.contradicciones.length > 0 ? ` En contra: ${o.contradicciones.map((c) => CONTRA_ES[c] ?? c).join(", ")}.` : ""}
      </span>
      {o.revision && o.revision.length > 0 ? <span style={{ color: C.amber }}>{textoRevision(o.revision)}</span> : null}
    </div>
  );
}

function TablaIdentidad({ a }: { a: ActorGrafoSiger }) {
  const filas = Object.entries(a.identidad).flatMap(([k, vs]) => vs.map((v) => ({ k, v })));
  if (filas.length === 0) return <p style={{ fontSize: 12, color: C.muted, margin: "6px 0" }}>Sin datos de identidad en las actas leídas.</p>;
  return (
    <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12, marginTop: 6 }}>
      <tbody>
        {filas.map(({ k, v }, i) => (
          <tr key={i} style={{ borderTop: `1px solid ${C.line}` }} title={v.cita ?? ""}>
            <td style={{ padding: "3px 6px", color: C.muted, whiteSpace: "nowrap" }}>{CAMPO_ES[k] ?? k}</td>
            <td style={{ padding: "3px 6px", fontWeight: 600 }}>{String(v.valor)}</td>
            <td style={{ padding: "3px 6px", color: C.muted }}>{ORIGEN_ES[v.origen ?? ""] ?? v.origen ?? ""}</td>
            <td style={{ padding: "3px 6px", color: C.muted }}>{`${v.archivo ?? ""}${v.pag != null ? ` · pág. ${String(v.pag)}` : ""}`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DetalleActor({ id, v, datos }: { id: string; v: VinculoSiger | null; datos: DatosGrafo }) {
  const a = datos.actores[id];
  if (!a) return null;
  const A = datos.actores;
  return (
    <div style={{ marginTop: 10, padding: 12, border: `1px solid ${C.line}`, borderRadius: 10, background: C.bg }}>
      <p style={{ margin: "0 0 6px", fontWeight: 700, color: C.ink }}>
        {a.nombre} <span style={{ fontWeight: 400, color: C.muted, fontSize: 12 }}>· {a.tipo} · documentos de {String(a.expedientes.length)} empresas</span>
      </p>
      {a.ofac.length > 0 ? a.ofac.map((o) => <BloqueOfac key={o.entidad_id} o={o} />) : (
        <p style={{ fontSize: 12.5, margin: "0 0 4px", color: C.muted }}>No coincide con nadie en la lista de OFAC.</p>
      )}
      {v ? (
        <>
          <p style={{ margin: "8px 0 4px", fontSize: 12, fontWeight: 700, color: C.ink }}>
            Cómo se relaciona · <span style={{ color: colorDe(v) }}>{textoTipo(v)}</span>
            <span style={{ fontWeight: 400, color: C.muted }}>
              {(() => { const e = v.tipo_explicacion ?? datos.totales_tipo?.find((t) => t.tipo === v.tipo)?.explicacion; return e ? ` — ${e}` : ""; })()}
              {v.posterior_listado ? " · vigente o posterior a la sanción de OFAC" : ""}
              {v.pct_ofac_total != null ? ` · según la última lista de socios, sancionados tenían ${String(v.pct_ofac_total)} % de la sociedad` : ""}
            </span>
          </p>
          {v.camino.map((l, i) => (
            <div key={i} style={{ fontSize: 12.5, lineHeight: 1.6 }}>
              {l.tipo === "participacion"
                ? <>{A[l.persona]?.nombre} — <b>{etiquetaLazo(l)}</b> → {A[l.sociedad]?.nombre}{terminado(l) ? <span style={{ color: C.muted }}> ({l.estado === "terminado_por_sustitucion" ? "sustituido" : "terminado"})</span> : null}</>
                : <>{A[l.de]?.nombre} — <b>{etiquetaLazo(l)}</b> → {A[l.a]?.nombre}</>}
              <Evidencia l={l} />
            </div>
          ))}
        </>
      ) : null}
      {(a.sat ?? []).length > 0 ? (
        <div style={{ margin: "8px 0 0" }}>
          <p style={{ margin: "0 0 2px", fontSize: 12, fontWeight: 700, color: C.ink }}>Listas del SAT</p>
          {[...(a.sat ?? [])].sort((x, y) => satOrden(x) - satOrden(y)).map((f, i) => (
            <div key={i} style={{ fontSize: 12.5, lineHeight: 1.6 }}>
              <b style={{ color: COLOR[tonoSat(f)] }}>{satTexto(f)}</b>
              <span style={{ color: C.muted }}>
                {`${f.fecha ? ` · desde ${fecha(f.fecha)}` : ""} · ${satCoincidencia(f)}`}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      <p style={{ margin: "10px 0 0", fontSize: 12, fontWeight: 700, color: C.ink }}>Identidad en las actas</p>
      <TablaIdentidad a={a} />
      {a.propiedad ? (
        <p style={{ fontSize: 12, color: C.ink, margin: "6px 0 0" }}>
          Última lista de socios ({a.propiedad.fecha_lista ? fecha(a.propiedad.fecha_lista) : "s/f"}, {a.propiedad.calidad_lista?.replace(/_/g, " ") ?? ""}):{" "}
          {a.propiedad.pct_ofac != null ? <b style={{ color: a.propiedad.pct_ofac >= 50 ? C.red : C.ink }}>{String(a.propiedad.pct_ofac)} % en manos de sancionados</b> : "sin sancionados identificados"}
        </p>
      ) : null}
      {a.alertas.length > 0 ? (
        <p style={{ fontSize: 11.5, color: C.amber, margin: "6px 0 0" }}>
          Alertas: {a.alertas.map((x) => x.texto ?? `${x.campo ?? ""} ${x.valor ?? ""} descartado (${x.motivo ?? ""})`).join(" · ")}
        </p>
      ) : null}
    </div>
  );
}

function DetalleAnclas({ datos }: { datos: DatosGrafo }) {
  const { anclas, otras } = gruposAnclas(datos.anclas);
  const fila = (x: DatosGrafo["anclas"][number]) => {
    const a = datos.actores[x.actor_id];
    if (!a) return null;
    const col = rojoDeIdentidad(x) ? C.red : esAncla(x.nivel) || x.nivel === "media" ? C.amber : C.muted;
    return (
      <div key={x.actor_id} style={{ borderTop: `1px solid ${C.line}`, padding: "6px 0" }}>
        <p style={{ margin: 0, fontSize: 12.5 }}>
          <b>{a.nombre}</b> — <b style={{ color: col }}>{NIVEL_ES[x.nivel] ?? x.nivel}</b>: {baseOfac(x.senales, x.revision).larga}
          {x.contradicciones.length > 0 ? <span style={{ color: C.muted }}>{` · en contra: ${x.contradicciones.map((c) => CONTRA_ES[c] ?? c).join(", ")}`}</span> : null}
          {x.revision && x.revision.length > 0 ? <span style={{ color: C.amber }}>{` ·${textoRevision(x.revision)}`}</span> : null}
          <span style={{ color: C.muted }}>{` · documentos de ${String(a.expedientes.length)} empresas${esAncla(x.nivel) ? "" : ` · ${NOTA_NO_ANCLA}`}`}</span>
        </p>
        {esAncla(x.nivel) ? <TablaIdentidad a={a} /> : null}
      </div>
    );
  };
  return (
    <div style={{ marginTop: 10, padding: 12, border: `1px solid ${C.line}`, borderRadius: 10, background: C.bg }}>
      {anclas.length > 0 ? (
        <>
          <p style={{ margin: "0 0 6px", fontWeight: 700, color: C.ink }}>Personas del Registro Público de Comercio que corresponden a esta entidad sancionada</p>
          {anclas.map(fila)}
        </>
      ) : null}
      {otras.length > 0 ? (
        <>
          <p style={{ margin: anclas.length > 0 ? "10px 0 6px" : "0 0 6px", fontWeight: 700, color: C.ink }}>
            Posibles coincidencias que no se usan como ancla
          </p>
          {otras.map(fila)}
        </>
      ) : null}
    </div>
  );
}
